import axios from 'axios';
import decode from 'jwt-decode';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import {
  A,
  Button,
  OtherStatusLabel,
  ReportTable,
  ReportTableCell,
  ReportTableCellEllipsis,
  ReportTableContent,
  ReportTableHeader,
  ReportTableRow,
  SpanFiller,
  Status,
} from '../../components/basic';
import SendEmailToClientWithResultModal from '../../components/SendEmailToClientWithResultModal';
import { backendUrl } from '../../constants';
import { isConsultant } from '../../helpers';

export default function VatCheck({
  match: {
    params: { report_id: reportId, company_id: companyId },
  },
  setIsLoading,
}) {
  const [reportDetails, setReportDetails] = useState();
  const [{ items: vatNumbers, count, limit }, setVatNumbers] = useState({
    items: [],
    count: 0,
    limit: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null, 'valid', 'invalid', 'loading', 'corrected'
  const [isLoadingVats, setIsLoadingVats] = useState(false);
  const mountedRef = useRef(true);

  const token = localStorage.getItem('auth');
  const user = decode(token);

  const fetchReportDetail = useCallback(async () => {
    const { data } = await axios.get(`${backendUrl}/companies/${companyId}/reports/${reportId}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (mountedRef.current) {
      setReportDetails(data);
    }
  }, [companyId, reportId]);

  const fetchVats = useCallback(async () => {
    const offset = (currentPage - 1) * limit;
    const params = { offset };
    
    // Add status filter if selected
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    if (mountedRef.current) {
      setIsLoadingVats(true);
    }
    try {
      const { data } = await axios.get(`${backendUrl}/companies/${companyId}/reports/${reportId}/vats`, {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (mountedRef.current) {
        setVatNumbers({
          items: data.items,
          count: data.count,
          limit,
        });
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingVats(false);
      }
    }
  }, [currentPage, companyId, reportId, limit, statusFilter]);

  useEffect(() => {
    let id;
    const fetch = async () => {
      fetchReportDetail();
      id = setTimeout(fetch, 5000);
    };

    fetch();

    return () => {
      clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, reportId]);

  useEffect(() => {
    fetchReportDetail();
    fetchVats();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, reportId, currentPage, statusFilter]);

  // Reset state when dataset changes (new report or company)
  useEffect(() => {
    // Clear old data immediately to avoid showing stale data
    setVatNumbers({
      items: [],
      count: 0,
      limit: 20,
    });
    setReportDetails(undefined);
    setCurrentPage(1);
    setStatusFilter(null);
  }, [companyId, reportId]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleStatusFilterChange = (e) => {
    const value = e.target.value === '' ? null : e.target.value;
    setStatusFilter(value);
  };

  const reloadVats = async () => {
    if (currentPage !== 1) {
      setCurrentPage(1); // This will trigger the useEffect to reload
    } else {
      // Already on page 1, manually trigger reload
      await fetchReportDetail();
      await fetchVats();
    }
  };

  const totalPages = Math.ceil(count / limit);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const fetchDownloadUrl = async () => {
    // Validate and sanitize IDs to prevent SSRF attacks
    if (!companyId || (typeof companyId !== 'string' && typeof companyId !== 'number')) {
      console.error('Invalid company ID');
      return;
    }
    if (!reportId || (typeof reportId !== 'string' && typeof reportId !== 'number')) {
      console.error('Invalid report ID');
      return;
    }
    
    const encodedCompanyId = encodeURIComponent(String(companyId));
    const encodedReportId = encodeURIComponent(String(reportId));
    
    // Get the blob directly from the backend
    const { data } = await axios.get(
      `${backendUrl}/companies/${encodedCompanyId}/reports/${encodedReportId}/download`,
      { 
        params: { offset: 0 },
        responseType: 'blob' // Tell axios to expect a blob response
      }
    );

    // Create download link with the blob
    const a = document.createElement('a');
    a.href = URL.createObjectURL(data);
    a.setAttribute('download', `result__${reportDetails.name}`);
    a.click();
    
    // Clean up the object URL
    URL.revokeObjectURL(a.href);
  };

  const regenerateReport = async () => {
    await axios.post(`${backendUrl}/companies/${companyId}/reports/${reportId}/regenerate`);
    await fetchReportDetail();
  };

  return reportDetails?.total > 0 ? (
    <>
      {isConsultant(user) && (
        <SendEmailToClientWithResultModal
          companyId={companyId}
          showEmailModal={showEmailModal}
          setShowEmailModal={setShowEmailModal}
          reportId={reportId}
          setIsLoading={setIsLoading}
        />
      )}
      <VatChecksContainer>
        <VatChecksContainerInfo>
          <VATChecksInfoTitle>VAT Checks</VATChecksInfoTitle>
          <Pie
            options={{
              legend: { display: false },
            }}
            data={{
              labels: [
                'Valid percentage',
                'Corrected percentage',
                'Errors',
                'Invalid percentage',
                'Invalid format percentage',
                'Loading percentage',
              ],
              datasets: [
                {
                  data: [
                    ((reportDetails.valid / reportDetails.total) * 100).toFixed(2),
                    ((reportDetails.corrected / reportDetails.total) * 100).toFixed(2),
                    ((reportDetails.errors / reportDetails.total) * 100).toFixed(2),
                    ((reportDetails.invalid / reportDetails.total) * 100).toFixed(2),
                    ((reportDetails.invalidFormat / reportDetails.total) * 100).toFixed(2),
                    ((reportDetails.loading / reportDetails.total) * 100).toFixed(2),
                  ],
                  backgroundColor: ['#6fb24f', '#98d67a', '#e0301e', '#e0301e', '#e0821e', '#afa8a9'],
                  borderWidth: 0.5,
                },
              ],
            }}
          />

          <VATChecksSummary>
            From the {reportDetails.total} VAT ID numbers checked: <br />
            <ul>
              <li>{reportDetails.valid} VAT ID numbers are valid</li>
              <li>{reportDetails.invalid} VAT ID numbers are invalid (VIES)</li>
              <li>{reportDetails.invalidFormat} VAT ID numbers have invalid format</li>
              <li>{reportDetails.errors} VAT ID numbers have errors (VIES)</li>
              <li>{reportDetails.corrected} VAT ID numbers have been corrected</li>
              <li>{reportDetails.loading} VAT ID numbers are loading</li>
            </ul>
          </VATChecksSummary>
          <VATChecksStatus>
            <Status status={reportDetails.status}>{reportDetails.status}</Status>
          </VATChecksStatus>
        </VatChecksContainerInfo>
        <VatChecksContainerVats>
          <VatChecksContainerVatsHeader>
            <VATChecksVatsTitle>Report Preview</VATChecksVatsTitle>
            <FilterContainer>
              <StatusSelect value={statusFilter || ''} onChange={handleStatusFilterChange}>
                <option value="">All</option>
                <option value="valid">Valid</option>
                <option value="invalid">Invalid</option>
                <option value="loading">Loading</option>
              </StatusSelect>
              <Button onClick={reloadVats} style={{ height: '36px' }}>
                Reload list
              </Button>
            </FilterContainer>
          </VatChecksContainerVatsHeader>
          <BlockUi blocking={isLoadingVats}>
            <ReportTable>
              <ReportTableHeader>
                <ReportTableCell width={'10%'}>Customer ID</ReportTableCell>
                <ReportTableCell width={'20%'}>Company Name</ReportTableCell>
                <ReportTableCell width={'40%'}>Company Information</ReportTableCell>
                <ReportTableCell width={'15%'}>Vat number</ReportTableCell>
                <ReportTableCell width={'15%'}>Status</ReportTableCell>
              </ReportTableHeader>
              <ReportTableContent>
                {vatNumbers.length === 0 ? (
                  reportDetails?.loading > 0 ? (
                    <ReportTableRow>
                      <ReportTableCell>Loading vat numbers</ReportTableCell>
                    </ReportTableRow>
                  ) : (
                    <ReportTableRow>
                      <ReportTableCell>No vat numbers found</ReportTableCell>
                    </ReportTableRow>
                  )
                ) : (
                  vatNumbers.map((vat) => (
                    <ReportTableRow key={vat.id}>
                      <ReportTableCell width={'10%'}>
                        {vat.internalCompanyId ? vat.internalCompanyId : <SpanFiller>not set</SpanFiller>}
                      </ReportTableCell>
                      <ReportTableCell width={'20%'}>
                        <ReportTableCellEllipsis>
                          {vat.companyName || vat.initialCompanyName || <SpanFiller>not set</SpanFiller>}
                        </ReportTableCellEllipsis>
                      </ReportTableCell>
                      <ReportTableCell width={'40%'}>
                        <ReportTableCellEllipsis>
                          {vat.companyAddress ||
                            ((vat.initialAddress || vat.initialZipcode || vat.initialCity || vat.initialCountry) &&
                              `${vat.initialAddress} ${vat.initialZipcode} ${vat.initialCity} ${vat.initialCountry}`) || (
                              <SpanFiller>not set</SpanFiller>
                            )}
                        </ReportTableCellEllipsis>
                      </ReportTableCell>
                      <ReportTableCell width={'15%'}>{vat.initialVatNumber}</ReportTableCell>
                      <ReportTableCell width={'15%'}>
                        {!reportDetails.orbisEnabled ? (
                          <>
                            <OtherStatusLabel
                              status={
                                vat.suggestions.length > 0
                                  ? 'corrected'
                                  : vat.isValid
                                  ? 'ready'
                                  : ['LOADING', 'PENDING'].includes(vat.viesStatus) ||
                                    ['LOADING', 'PENDING'].includes(vat.orbisStatus)
                                  ? 'loading'
                                  : 'failed'
                              }
                              data-tooltip-id={`vat-tooltip-${vat.id}`}
                              data-tooltip-content={
                                vat.viesErrorMessage || vat.tryCount != null
                                  ? [vat.viesErrorMessage, vat.tryCount != null ? `Try count: ${vat.tryCount}` : null]
                                    .filter(Boolean)
                                    .join('\n')
                                  : undefined
                              }
                            >
                              {' '}
                              {vat.suggestions.length > 0
                                ? 'corrected'
                                : vat.isValid
                                ? 'Valid'
                                : ['LOADING', 'PENDING'].includes(vat.viesStatus) ||
                                  ['LOADING', 'PENDING'].includes(vat.orbisStatus) ||
                                  vat.isValid == null
                                ? 'Loading'
                                : 'Invalid'}{' '}
                            </OtherStatusLabel>
                            {(vat.viesErrorMessage || vat.tryCount != null) && (
                              <Tooltip 
                                id={`vat-tooltip-${vat.id}`} 
                                style={{ 
                                  maxWidth: '300px', 
                                  whiteSpace: 'pre-line', 
                                  zIndex: 9999,
                                  backgroundColor: '#333',
                                  color: '#fff',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }} 
                              />
                            )}
                          </>
                        ) : (
                          <Link to={`/${companyId}/vat-checks/${reportId}/remediation/${vat.id}`}>
                            {vat.orbisStatus !== 'DONE' || vat.suggestions.length > 0 ? (
                              <>
                                <OtherStatusLabel
                                  status={
                                    vat.suggestions.length > 0
                                      ? 'corrected'
                                      : vat.isValid
                                      ? 'ready'
                                      : ['LOADING', 'PENDING'].includes(vat.viesStatus) ||
                                        ['LOADING', 'PENDING'].includes(vat.orbisStatus) ||
                                        vat.isValid == null
                                      ? 'loading'
                                      : 'failed'
                                  }
                                  data-tooltip-id={`vat-tooltip-${vat.id}`}
                                  data-tooltip-content={
                                    vat.viesErrorMessage || vat.tryCount != null
                                      ? [vat.viesErrorMessage, vat.tryCount != null ? `Try count: ${vat.tryCount}` : null]
                                        .filter(Boolean)
                                        .join('\n')
                                      : undefined
                                  }
                                >
                                  {' '}
                                  {vat.suggestions.length > 0
                                    ? 'corrected'
                                    : vat.isValid
                                    ? 'Valid'
                                    : ['LOADING', 'PENDING'].includes(vat.viesStatus) ||
                                      ['LOADING', 'PENDING'].includes(vat.orbisStatus) ||
                                      vat.isValid == null
                                    ? 'Loading'
                                    : 'Invalid'}{' '}
                                </OtherStatusLabel>
                                {(vat.viesErrorMessage || vat.tryCount != null) && (
                                  <Tooltip 
                                    id={`vat-tooltip-${vat.id}`} 
                                    style={{ 
                                      maxWidth: '300px', 
                                      whiteSpace: 'pre-line', 
                                      zIndex: 9999,
                                      backgroundColor: '#333',
                                      color: '#fff',
                                      padding: '8px 12px',
                                      borderRadius: '4px',
                                      fontSize: '12px'
                                    }} 
                                  />
                                )}
                              </>
                            ) : (
                              <RemediationSpan>Go to remediation</RemediationSpan>
                            )}
                          </Link>
                        )}
                      </ReportTableCell>
                    </ReportTableRow>
                  ))
                )}
              </ReportTableContent>
            </ReportTable>
            {count > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, count)} of {count} results
                </PaginationInfo>
                <PaginationControls>
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ height: '36px', minWidth: '80px' }}
                  >
                    Previous
                  </Button>
                  <PageNumbers>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        );
                      })
                      .map((page, index, array) => {
                        // Add ellipsis between non-consecutive pages
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && <Ellipsis>...</Ellipsis>}
                            <PageButton
                              onClick={() => handlePageChange(page)}
                              active={currentPage === page}
                            >
                              {page}
                            </PageButton>
                          </React.Fragment>
                        );
                      })}
                  </PageNumbers>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{ height: '36px', minWidth: '80px' }}
                  >
                    Next
                  </Button>
                </PaginationControls>
              </PaginationContainer>
            )}
          </BlockUi>
          <ReportActions>
            <Actions>
              {reportDetails.shouldRegenerateFile ? (
                <Button onClick={() => regenerateReport()}>Regenerate report</Button>
              ) : (
                <>
                  {reportDetails.status === 'ready' && (
                    <>
                      <DownloadBtn onClick={fetchDownloadUrl}>Download full results</DownloadBtn>
                      {isConsultant(user) && (
                        <SendToClientBtn onClick={() => setShowEmailModal(true)}>Send to client</SendToClientBtn>
                      )}
                    </>
                  )}
                </>
              )}
            </Actions>
          </ReportActions>
        </VatChecksContainerVats>
      </VatChecksContainer>
    </>
  ) : (
    <span>Loading...</span>
  );
}

const RemediationSpan = styled.div`
  color: #eb8c00;
`;

const VatChecksContainer = styled.div`
  display: flex;
`;
const VatChecksContainerInfo = styled.div`
  background: white;
  margin-right: 20px;
  padding: 10px 26px;
`;
const VatChecksContainerVats = styled.div`
  background: white;
  flex: 1;
  padding: 10px 26px;
  & h1 {
    margin-bottom: 0;
  }
`;

const VatChecksContainerVatsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusSelect = styled.select`
  font-size: 14px;
  border: solid 1px #979797;
  padding: 7px 10px;
  border-radius: 3px;
  background-color: white;
  color: #474747;
  cursor: pointer;
  outline: none;
  height: 36px;
  box-sizing: border-box;

  &:hover {
    border-color: #e0301e;
  }

  &:focus {
    border-color: #e0301e;
  }
`;

const ReportActions = styled.div`
  margin-top: 20px;
`;

const SendToClientBtn = styled(Button)`
  font-family: HelveticaNeue;
`;

const DownloadBtn = styled(A)`
  margin-right: 10px;
  font-size: 16px;
  cursor: pointer;
`;

const Actions = styled.div`
  text-align: right;
`;

const VATChecksSummary = styled.div`
  margin-top: 50px;
  font-size: 18px;
  color: #474747;
`;

const VATChecksStatus = styled.div`
  display: flex;
  justify-content: center;
`;

const Title = styled.h1`
  color: #474747;
  margin: 0;
  font-size: 36px;
  font-weight: 500;
`;

const VATChecksInfoTitle = styled(Title)`
  margin-bottom: 20px;
  text-align: center;
  font-family: 'Charter';
`;

const VATChecksVatsTitle = styled(Title)`
  margin-bottom: 20px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 15px 0;
  border-top: 1px solid #d8d8d8;
`;

const PaginationInfo = styled.div`
  color: #474747;
  font-size: 14px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PageButton = styled.button`
  border: 1px solid ${({ active }) => (active ? '#e0301e' : '#d8d8d8')};
  color: ${({ active }) => (active ? 'white' : '#474747')};
  background-color: ${({ active }) => (active ? '#e0301e' : 'white')};
  padding: 7px 12px;
  min-width: 40px;
  text-align: center;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  border-radius: 3px;

  &:hover {
    background-color: ${({ active }) => (active ? '#e0301e' : '#f5f5f5')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  color: #474747;
  padding: 0 5px;
`;
